import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { chatService, type ChatMessage, type ClientAction, type ChatContext, type ConversationSummary, type ModelTier, type PageBuildState } from '@/services/chatService'

const MODEL_TIER_KEY = 'pj-chat-model-tier'
function storedTier(): ModelTier {
  try {
    const value = localStorage.getItem(MODEL_TIER_KEY)
    if (value === 'economy' || value === 'balanced' || value === 'quality' || value === 'chatgpt') return value
  } catch { /* storage unavailable */ }
  return 'balanced'
}

// The email ProfitJam Support sends when the team replies while you are away
// links back to the app with #chat=<conversation token>. The token is read
// once, as the app starts and before any redirect can drop it from the address.
function chatTokenIn(hash: string): string | null {
  if (!hash || hash.length < 2) return null
  try {
    return new URLSearchParams(hash.slice(1)).get('chat') || null
  } catch {
    return null
  }
}
let linkedChatToken: string | null = typeof window === 'undefined' ? null : chatTokenIn(window.location.hash)

export const useAIAssistantStore = defineStore('aiAssistant', () => {
  const router = useRouter()

  // Panel state
  const isOpen = ref(false)
  const panelWidth = ref(400)

  // Conversations
  const conversations = ref<ConversationSummary[]>([])
  const activeConversationId = ref<number | null>(null)
  const activeConversationToken = ref<string | null>(null)
  // Title shown in the panel header — null until the backend's Haiku naming
  // pass fires an SSE 'title' event (the header shows "New chat" meanwhile).
  const activeSubject = ref<string | null>(null)

  // Messages
  const messages = ref<ChatMessage[]>([])
  const isLoading = ref(false)
  const isAiTyping = ref(false)

  // View state
  const showConversationList = ref(false)

  // Builder actions (queue for multi-section edits)
  const pendingBuilderActions = ref<{ command: string; params: Record<string, unknown> }[]>([])

  // Escalation
  const isEscalated = ref(false)

  // Price/quality choice, remembered per browser and sent with every request.
  const modelTier = ref<ModelTier>(storedTier())
  function setModelTier(tier: ModelTier) {
    modelTier.value = tier
    try { localStorage.setItem(MODEL_TIER_KEY, tier) } catch { /* storage unavailable */ }
  }
  const withTier = (context: ChatContext): ChatContext => ({ ...context, modelTier: modelTier.value })

  // SSE
  let eventSource: EventSource | null = null
  // A `redirect` action's optional follow-up line — flushed as its own chat
  // message right after the turn's final message lands, so it shows in order.
  let pendingRedirectPrompt: string | null = null

  // Computed
  const hasActiveConversation = computed(() => activeConversationId.value !== null)

  // Panel actions
  function openPanel() {
    isOpen.value = true
  }

  function closePanel() {
    isOpen.value = false
    showConversationList.value = false
  }

  function togglePanel() {
    isOpen.value = !isOpen.value
  }

  // Open the panel AND start a new conversation seeded with `message`.
  // Used by the BR "Falar com vendas" / "Contact sales" CTAs to drop the
  // user straight into an Anna conversation about the full website
  // builder. The separate /sales-escalation endpoint is called by the
  // caller before this — Slack alert + chat open are independent fires.
  async function openPanelWithMessage(message: string, context: ChatContext) {
    openPanel()
    newConversation()
    await startConversation(message, context)
  }

  // SSE management
  function connectSSE(conversationId: number) {
    disconnectSSE()
    eventSource = chatService.createStream(conversationId)

    // Reload persisted replies after subscribing/reconnecting. Page writes are
    // server-side, so recovery never replays a mutation through the browser.
    const recoverMessages = async () => {
      try {
        const { data } = await chatService.getMessages(conversationId)
        if (activeConversationId.value !== conversationId) return
        const latestBefore = Math.max(0, ...messages.value.map(message => message.id))
        for (const message of data.messages) {
          const existing = messages.value.find(item => item.id === message.id)
          if (!existing) messages.value.push(message)
          // A page build keeps changing after it is first shown.
          else if (message.metadata?.pageBuild) Object.assign(existing, { content: message.content, metadata: message.metadata })
        }
        messages.value = [...messages.value.filter(message => message.id > 0).sort((a, b) => a.id - b.id), ...messages.value.filter(message => message.id <= 0)]
        const latestLoaded = Math.max(0, ...data.messages.map(message => message.id))
        if (latestLoaded >= latestBefore && data.status) {
          isAiTyping.value = data.status === 'waiting_on_ai'
          isEscalated.value = data.status === 'escalated'
          if (!isAiTyping.value) messages.value = messages.value.filter(message => message.contentType !== 'status')
        }
      } catch { /* EventSource keeps reconnecting; the conversation can be reopened. */ }
    }
    eventSource.addEventListener('open', recoverMessages)

    eventSource.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data) as ChatMessage
        // Clear the transient flow-status line before adding the final message.
        // Confirmation cards and redirect-prompt lines are kept — they persist
        // until the user acts on them.
        messages.value = messages.value.filter(m => m.contentType !== 'status')

        // Avoid duplicates (user messages are already added optimistically)
        if (!messages.value.some(m => m.id === data.id)) {
          messages.value.push(data)
        }

        // A redirect action this turn queued a follow-up line — show it now,
        // after the final message so it reads in order.
        if (pendingRedirectPrompt) {
          messages.value.push({
            id: -Date.now(),
            role: 'ai',
            content: pendingRedirectPrompt,
            contentType: 'text',
            senderName: 'Tempo',
            createdAt: new Date().toISOString()
          })
          pendingRedirectPrompt = null
        }

        // Handle client actions from AI response (these may already have been streamed,
        // but some could be new if the streaming didn't cover them)
        if (data.actions && data.actions.length > 0) {
          handleClientActions(data.actions)
        }

        isAiTyping.value = false
      } catch (e) {
        console.error('[AIAssistant] SSE message parse error:', e)
      }
    })

    // Handle streamed clientActions in real-time (during tool loop)
    eventSource.addEventListener('action', (event) => {
      try {
        const clientAction = JSON.parse(event.data) as ClientAction
        handleClientActions([clientAction])
      } catch (e) {
        console.error('[AIAssistant] SSE action parse error:', e)
      }
    })

    // Handle progress events — canned flow-status lines emitted per tool call
    // while the tool loop runs (the backend never streams raw model text on the
    // merchant path). Rendered as a single muted status line that replaces the
    // previous one, so the user sees a calm, readable flow rather than a flicker
    // of half-formed reasoning.
    eventSource.addEventListener('progress', (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.text) {
          const progressId = -Date.now()
          // Replace the previous flow-status line (everything else stays)
          messages.value = messages.value.filter(m => m.contentType !== 'status')
          messages.value.push({
            id: progressId,
            role: 'ai',
            content: data.text,
            contentType: 'status',
            senderName: 'Tempo',
            createdAt: new Date().toISOString()
          })
        }
      } catch (e) {
        // ignore
      }
    })

    eventSource.addEventListener('status', (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.status === 'escalated') {
          isEscalated.value = true
        }
      } catch (e) {
        // ignore
      }
    })

    // Live progress of a page being built in the background (build_page).
    eventSource.addEventListener('build', (event) => {
      try {
        const data = JSON.parse(event.data) as { messageId: number; content: string; pageBuild: PageBuildState }
        const existing = messages.value.find(m => m.id === data.messageId)
        if (existing) {
          existing.content = data.content
          existing.metadata = { ...(existing.metadata || {}), pageBuild: data.pageBuild }
        } else {
          const status = messages.value.filter(m => m.contentType === 'status')
          messages.value = [...messages.value.filter(m => m.contentType !== 'status'), {
            id: data.messageId, role: 'ai', content: data.content, contentType: 'text', senderName: 'Tempo',
            metadata: { pageBuild: data.pageBuild }, createdAt: new Date().toISOString()
          }, ...status]
        }
      } catch {
        // ignore
      }
    })

    eventSource.addEventListener('title', (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.subject) activeSubject.value = data.subject
      } catch {
        // ignore
      }
    })

    eventSource.addEventListener('ping', () => {
      // Recover a persisted reply if its event was lost while the stream stayed open.
      if (isAiTyping.value) void recoverMessages()
    })

    eventSource.onerror = () => {
      console.warn('[AIAssistant] SSE connection error, will retry...')
    }
  }

  function disconnectSSE() {
    if (eventSource) {
      eventSource.close()
      eventSource = null
    }
  }

  // Resolve a backend-supplied route and navigate.
  //   Editor routes (`/<pageId>/editor`): always router.push the full
  //     `/<workspaceId>/<pageId>/editor` path. PageEditorPage mounts
  //     `<Builder :key="currentPageId">`, so a same-shape route swap
  //     remounts the builder onto the new page (the older "hot-swap via
  //     ai-navigate-page builder command" path no longer exists).
  //   Other routes: workspace-prefix and push.
  function goToRoute(route: string) {
    const currentPath = router.currentRoute.value.path
    const workspaceMatch = currentPath.match(/^\/(\d+)/)
    const workspaceId = workspaceMatch ? workspaceMatch[1] : ''
    const editorTarget = route.match(/^\/(\d+)\/editor/)

    if (editorTarget && workspaceId) {
      const pageId = editorTarget[1]
      router.push(`/${workspaceId}/${pageId}/editor`)
      return
    }

    // Idempotent workspace prefixing. Anna's `redirect` tool and markdown
    // links both flow through here; both sources sometimes include the
    // workspace ID already (`/21/subscription`) and sometimes don't
    // (`/subscription`). Blindly prepending produced `/21/21/subscription` →
    // 404. Detect a leading numeric segment and skip the prefix when present.
    const alreadyPrefixed = /^\/\d+(\/|$)/.test(route)
    const prefix = workspaceId && !alreadyPrefixed ? `/${workspaceId}` : ''
    const fullRoute = route.startsWith('/') ? `${prefix}${route}` : route
    router.push(fullRoute)
  }

  // Client action handling
  function handleClientActions(actions: ClientAction[]) {
    for (const action of actions) {
      switch (action.type) {
        case 'navigate':
        case 'redirect': {
          if (action.route) goToRoute(action.route)
          // redirect may carry a short follow-up line — shown after the
          // turn's final message (see the 'message' SSE handler).
          if (action.prompt) pendingRedirectPrompt = action.prompt
          break
        }
        case 'builder_command': {
          pendingBuilderActions.value.push({
            command: action.command || '',
            params: { ...((action.params as Record<string, unknown>) || {}), actionId: action.actionId, conversationId: activeConversationId.value }
          })
          break
        }
        case 'request_confirmation': {
          // Render a preview card with Accept / Reject / Modify. The user must
          // act on a separate turn before the corresponding apply runs.
          messages.value.push({
            id: -Date.now(),
            role: 'ai',
            content: '',
            contentType: 'confirmation',
            senderName: 'Tempo',
            createdAt: new Date().toISOString(),
            confirmation: {
              intent: action.intent || '',
              sectionId: action.sectionId,
              selectionToken: action.selectionToken,
              preview: action.preview
            }
          })
          break
        }
      }
    }
  }

  function shiftPendingBuilderAction() {
    pendingBuilderActions.value.shift()
  }

  // Tell the server whether a builder command actually applied. The backend uses
  // this to refund the merchant for a billable change that silently failed to
  // land (see appChatController.builderAck). Fire-and-forget — a failed ack must
  // never disrupt the editor.
  function ackBuilderAction(actionId: string, ok: boolean, reason?: string, conversationId?: number) {
    const convId = conversationId ?? activeConversationId.value
    if (!convId || !actionId) return
    chatService.builderAck(convId, actionId, ok, reason).catch(() => { /* best-effort */ })
  }

  // Conversation actions
  async function startConversation(message: string, context: ChatContext) {
    isLoading.value = true
    isAiTyping.value = true

    try {
      const { data } = await chatService.createConversation(message, withTier(context))
      activeConversationId.value = data.conversationId
      activeConversationToken.value = data.conversationToken

      // Add user message optimistically
      messages.value = [{
        id: data.message.id,
        role: 'user',
        content: message,
        contentType: 'text',
        senderName: data.message.senderName,
        createdAt: data.message.createdAt
      }]

      showConversationList.value = false

      // Connect SSE for AI response
      connectSSE(data.conversationId)
    } catch (error) {
      console.error('[AIAssistant] Failed to start conversation:', error)
      isAiTyping.value = false
    } finally {
      isLoading.value = false
    }
  }

  async function sendMessage(content: string, context: ChatContext) {
    if (!activeConversationId.value || isLoading.value || isAiTyping.value) return

    isAiTyping.value = true

    try {
      const { data } = await chatService.sendMessage(activeConversationId.value, content, withTier(context))

      // Add user message optimistically (SSE will handle AI response)
      if (!messages.value.some(m => m.id === data.message.id)) {
        messages.value.push({
          id: data.message.id,
          role: 'user',
          content,
          contentType: 'text',
          senderName: data.message.senderName,
          createdAt: data.message.createdAt
        })
      }
    } catch (error) {
      console.error('[AIAssistant] Failed to send message:', error)
      const failure = error as { response?: { status?: number } }
      isAiTyping.value = failure.response?.status === 409
      messages.value.push({ id: -Date.now(), role: 'ai', contentType: 'status', senderName: 'Tempo',
        content: context.locale === 'br'
          ? 'Não consegui enviar esta mensagem. Aguarde a resposta atual e tente novamente.'
          : 'I could not send this message. Wait for the current reply and try again.',
        createdAt: new Date().toISOString() })
    }
  }

  async function loadConversations() {
    try {
      const { data } = await chatService.listConversations()
      conversations.value = data.conversations
    } catch (error) {
      console.error('[AIAssistant] Failed to load conversations:', error)
    }
  }

  async function selectConversation(conversationId: number) {
    pendingBuilderActions.value = []
    activeConversationId.value = conversationId
    showConversationList.value = false
    isLoading.value = true
    isEscalated.value = false

    try {
      const { data } = await chatService.getMessages(conversationId)
      if (activeConversationId.value !== conversationId) return
      messages.value = data.messages
      isAiTyping.value = data.status === 'waiting_on_ai'
      // Check if this conversation was already escalated
      const conv = conversations.value.find(c => c.id === conversationId)
      activeSubject.value = conv?.subject ?? null
      if (conv?.status === 'escalated') {
        isEscalated.value = true
      }
      connectSSE(conversationId)
    } catch (error) {
      console.error('[AIAssistant] Failed to load messages:', error)
    } finally {
      isLoading.value = false
    }
  }

  function newConversation() {
    disconnectSSE()
    activeConversationId.value = null
    activeConversationToken.value = null
    activeSubject.value = null
    messages.value = []
    pendingBuilderActions.value = []
    showConversationList.value = false
    isAiTyping.value = false
    isEscalated.value = false
  }

  function showHistory() {
    showConversationList.value = true
    loadConversations()
  }

  function hideHistory() {
    showConversationList.value = false
  }

  async function sendSystemHint(hint: string, ctx: ChatContext) {
    // Always start a fresh conversation for system hints
    newConversation()

    const hintContext = { ...ctx, systemHint: hint }
    isAiTyping.value = true

    try {
      const { data } = await chatService.createConversation('', withTier(hintContext))
      activeConversationId.value = data.conversationId
      activeConversationToken.value = data.conversationToken
      messages.value = []
      showConversationList.value = false
      connectSSE(data.conversationId)
    } catch (error) {
      console.error('[AIAssistant] Failed to send system hint:', error)
      isAiTyping.value = false
    }
  }

  // Opened from that email link: take the token out of the address bar, then
  // open the panel on that conversation. Only the signed-in teammate's own
  // conversations open this way: the token is matched against their list.
  async function openFromLink() {
    const token = linkedChatToken
    linkedChatToken = null
    if (!token) return
    await router.isReady()
    const current = router.currentRoute.value
    if (chatTokenIn(current.hash)) {
      const rest = new URLSearchParams(current.hash.slice(1))
      rest.delete('chat')
      const hash = rest.toString()
      await router.replace({ path: current.path, query: current.query, hash: hash ? `#${hash}` : '' })
    }
    await loadConversations()
    const match = conversations.value.find(c => c.token === token)
    if (!match) return
    openPanel()
    await selectConversation(match.id)
    if (activeConversationId.value === match.id) activeConversationToken.value = match.token
  }

  // Continue a stopped page build, or stop a running one.
  async function resumePageBuild(messageId: number) {
    if (!activeConversationId.value) return
    await chatService.resumePageBuild(activeConversationId.value, messageId, modelTier.value)
  }
  async function cancelPageBuild(messageId: number) {
    if (!activeConversationId.value) return
    await chatService.cancelPageBuild(activeConversationId.value, messageId)
  }

  return {
    // State
    isOpen,
    panelWidth,
    conversations,
    activeConversationId,
    activeConversationToken,
    activeSubject,
    messages,
    isLoading,
    isAiTyping,
    isEscalated,
    showConversationList,
    pendingBuilderActions,
    modelTier,
    // Computed
    hasActiveConversation,
    // Actions
    openPanel,
    closePanel,
    togglePanel,
    openPanelWithMessage,
    openFromLink,
    startConversation,
    sendMessage,
    loadConversations,
    selectConversation,
    newConversation,
    showHistory,
    hideHistory,
    sendSystemHint,
    handleClientActions,
    shiftPendingBuilderAction,
    ackBuilderAction,
    disconnectSSE,
    goToRoute,
    setModelTier,
    resumePageBuild,
    cancelPageBuild
  }
})
